import {useHalCollection, useHalStates} from '@synako/haligator';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Box, Image, Spinner, Stack, Text, VStack} from '@chakra-ui/react';
import {useClient} from '@/haligator-factory';
import {useMarketplaceBrandColors} from '@/hooks/marketplace/useMarketplaceBrandColors';
import {PanierFound} from './PanierFound';
import {CatalogueMarketplace} from 'api-types/ffconnect';
import {Left404} from './PanierNotFound/Left404';
import {routes} from 'ff-common';

export const PanierMarketplaceView: React.FC = () => {
  const {marketplace_id} = useParams<{marketplace_id: CatalogueMarketplace}>();
  const [searchParams] = useSearchParams();
  const dateDeLivraisonFromUrl = searchParams.get('date_de_livraison');
  const {t} = useTranslation('common');
  const client = useClient();
  const navigate = useNavigate();

  const marketplacesData = useHalCollection(client.follow('marketplaces'));
  const marketplaceData = marketplacesData.items?.find(
    (item) => item.data.marketplace_id === marketplace_id,
  )?.data;
  const {couleur, textCouleur} = useMarketplaceBrandColors(marketplace_id);

  const paniersHook = useHalStates(
    client
      .follow('paniers', {type_panier: marketplace_id, statut: 'ouvert'})
      .followAll('items'),
  );

  if (!marketplace_id || !marketplaceData) {
    return <Left404 t={t} />;
  }

  const normalizeDate = (d: string) => d.slice(0, 10);
  const panierState = dateDeLivraisonFromUrl
    ? (paniersHook.states?.find(
        (s) =>
          normalizeDate(s.data.livraison_tolede.date_de_livraison) ===
          normalizeDate(dateDeLivraisonFromUrl),
      ) ?? paniersHook.states?.[0])
    : paniersHook.states?.[0];

  if (paniersHook.loading) {
    return (
      <VStack
        w='100%'
        minH='80vh'
        justify='center'
        alignItems='center'
      >
        <Spinner
          size='xl'
          color='blue.main'
        />
      </VStack>
    );
  }

  if (paniersHook.error) {
    return (
      <VStack
        w='100%'
        minH='80vh'
        justify='center'
        alignItems='center'
        p={8}
      >
        <Text color='red.500'>{t('marketplace.erreur_chargement_panier')}</Text>
      </VStack>
    );
  }

  if (panierState) {
    return <PanierFound panier={panierState} />;
  }

  return (
    <VStack
      p={{base: 0, md: 12}}
      w='100%'
      gap='0'
    >
      <Stack
        direction={{base: 'column', md: 'row'}}
        bg='white'
        w='100%'
        minH='80vh'
        py={{base: 0, md: 20}}
        justify={{base: 'start', md: 'center'}}
        alignItems='stretch'
        gap='5'
      >
        <Left404 t={t} />
        <VStack
          alignItems={{base: 'stretch', md: 'start'}}
          p='6'
          width={{base: '95%', md: '40%'}}
        >
          <Text
            as='h2'
            fontSize={{base: 'xl', md: '3xl'}}
            fontWeight='700'
            w={{base: '100%', md: 'initial'}}
            marginBottom={10}
          >
            {t('panier.panier_vide_cta')}
          </Text>
          <Box
            width='100%'
            display='flex'
            flexDirection='column'
            gap='20px'
          >
            <VStack
              gap={4}
              alignItems='flex-start'
              bgColor={couleur}
              // backgroundImage={
              //   marketplaceData?.image_principale
              //     ? `url(${import.meta.env['VITE_MEDIA_URL']}${marketplaceData.image_principale})`
              //     : undefined
              // }
              backgroundSize='contain'
              backgroundPosition='right'
              backgroundRepeat='no-repeat'
              _hover={{opacity: 0.8}}
              py={7}
              px='40px'
              borderRadius='md'
              cursor='pointer'
              onClick={() =>
                navigate(
                  `${routes.client.catalogue.path}?marketplace_id=${marketplace_id}`,
                )
              }
            >
              {marketplaceData.logo_horizontal && (
                <Image
                  src={`${import.meta.env['VITE_MEDIA_URL']}${marketplaceData.logo_horizontal}`}
                  alt={marketplaceData.nom}
                  h='40px'
                  objectFit='contain'
                />
              )}
              {marketplaceData.description && (
                <Text
                  fontSize='sm'
                  color={textCouleur}
                >
                  {marketplaceData.description}
                </Text>
              )}
            </VStack>
          </Box>
        </VStack>
      </Stack>
    </VStack>
  );
};
